import React, { useCallback, useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import loadable from 'components/utils/lazyImport'
import MarkdownRender from 'LuxKit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import PropertyReview from './PropertyReview'
import LayoutContainer from 'components/Common/LayoutContainer'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import FinePrint from '../../Common/FinePrint'
import TraderInformation from './TraderInformation'
import BodyText from 'LuxKit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import UserReviewList from './UserReviewList'
import { isBundleOffer } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import { scrollToId } from 'lib/window/scrollUtils'
import UserReviewCarousel from './UserReviewCarousel'
import { OptimizelyExperiments } from 'constants/optimizely'
import { useDeferredAnalyticsOptimizely } from 'lib/optimizely/useDeferredAnalyticsOptimizely'
import { trackClientEvent } from 'analytics/analytics'
import { useInView } from 'react-intersection-observer'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import { getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'

const PropertyMap = loadable(() => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap'))

interface Props {
  hash?: string
  offerDetails: App.Offer
  traderInformation?: App.TraderInformation
  mobileHotelOfferPageTest?: boolean
}

const StyledFacilities = styled(MarkdownRender)`
  > ul {
    columns: auto 2;
  }
`

const READ_MORE_TRACKING_MAP: Record<string, string> = {
  'getting-there': 'offer_page_read_more_location',
  highlights: 'offer_page_read_more_highlights',
  facilities: 'offer_page_read_more_facilities',
  'fine-print': 'offer_page_read_more_fine_print',
}

function packageHasPropertyWithCoords(offer: App.Offer) {
  return !!offer.property?.latitude && !!offer.property?.longitude
}

interface ContentField {
  id: string
  label: string
  open: boolean
  collapsable: boolean
  initialHeight?: number
  minimumContentHeight?: number
  contentComponent: React.ReactElement | Array<React.ReactElement>
}

function getContentFields(props: Props, isNewReviewsPanelEnabled: boolean, shouldHideHighlights: boolean): Array<ContentField> {
  const {
    hash,
    offerDetails,
    mobileHotelOfferPageTest,
  } = props

  const {
    highlights,
    facilities,
    finePrint,
    gettingThere,
    lowestPricePackage,
    property,
  } = offerDetails

  const contentFields: Array<ContentField> = []
  if (packageHasPropertyWithCoords(offerDetails)) {
    const markers: Array<App.MapMarker> = []

    if (isBundleOffer(offerDetails)) {
      const bundleOffers = Object.values(offerDetails.bundledOffers)
      bundleOffers.forEach((bundleOffer) => {
        markers.push({
          id: bundleOffer.property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: bundleOffer.property.latitude,
          longitude: bundleOffer.property.longitude,
        })
      })
    } else {
      if (property?.latitude && property?.longitude) {
        markers.push({
          id: property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: property.latitude,
          longitude: property.longitude,
        })
      }
    }

    const bundledOffers = Object.values(offerDetails.bundledOffers ?? {})

    contentFields.push({
      id: 'getting-there',
      label: 'Location',
      open: hash === '#getting-there',
      collapsable: true,
      initialHeight: 410,
      contentComponent: <Group gap={16} direction="vertical">
        <PropertyMap
          height={258}
          wrapperStyle={{ height: 258 }}
          markers={markers}
        />
        {isBundleOffer(offerDetails) && <Group gap={16} direction="vertical">
          <div>
            <BodyText variant="medium">{bundledOffers[0].property?.name}</BodyText>
            <BodyText variant="medium" weight="bold">{bundledOffers[0].property?.address}</BodyText>
          </div>
          <div>
            <BodyText variant="medium">{bundledOffers[1].property?.name}</BodyText>
            <BodyText variant="medium" weight="bold">{bundledOffers[1].property?.address}</BodyText>
          </div>
        </Group>}
        {!isBundleOffer(offerDetails) && <div>
          <BodyText variant="medium">{property?.name}</BodyText>
          <BodyText variant="medium" weight="bold">{property?.address}</BodyText>
        </div>}
        <MarkdownRender fontSize="medium" type="compact" content={gettingThere} />
      </Group>,
    })
  }

  if (highlights && !mobileHotelOfferPageTest && !shouldHideHighlights) {
    contentFields.push({
      id: 'highlights',
      label: 'Highlights',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={highlights} />,
      open: hash === '#highlights',
    })
  }

  if (facilities && !mobileHotelOfferPageTest) {
    contentFields.push({
      id: 'facilities',
      label: 'Facilities',
      open: hash === '#facilities',
      collapsable: true,
      initialHeight: 120,
      contentComponent: <StyledFacilities type="compact" fontSize="medium" content={facilities} />,
    })
  }

  if (gettingThere && !packageHasPropertyWithCoords(offerDetails)) {
    contentFields.push({
      id: 'getting-there',
      label: 'Location',
      open: hash === '#getting-there',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={gettingThere} />,
    })
  }

  if (lowestPricePackage?.tour?.itinerary) {
    contentFields.push({
      id: 'itinerary',
      label: 'Itinerary',
      open: hash === '#itinerary',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={lowestPricePackage.tour.itinerary} />,
    })
  }

  if (finePrint) {
    contentFields.push({
      id: 'fine-print',
      label: 'Fine Print',
      open: !!offerDetails.property?.taxesAndFeesContent || hash === '#fine-print',
      collapsable: true,
      contentComponent: <FinePrint finePrint={finePrint} offer={offerDetails} />,
    })
  }

  if (lowestPricePackage?.tour?.reviews.length) {
    contentFields.push({
      id: 'customer-reviews',
      label: 'Reviews',
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: lowestPricePackage.tour.reviews.map(review =>
        <PropertyReview
          key={review.id}
          content={review.content}
          source={review.source}
        />,
      ),
    })
  }

  if (!isBundleOffer(offerDetails) && property?.rating?.reviewsTotal && property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: 'Reviews',
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: isNewReviewsPanelEnabled ? <UserReviewCarousel property={property} /> : <UserReviewList property={property} />,
    })
  }

  return contentFields
}

function NavigableOfferContent(props: Props) {
  const { variation: reviewsPanelVariation, triggerAnalytics: triggerReviewsExperiment } =
    useDeferredAnalyticsOptimizely(OptimizelyExperiments.croNewReviewsPanel)
  const isNewReviewsPanelEnabled = !!reviewsPanelVariation

  const {
    variation: removeHighlightsVariation,
    triggerAnalytics: triggerRemoveHighlightsAnalytics,
  } = useDeferredAnalyticsOptimizely(OptimizelyExperiments.croRemoveFlashHighlightsContentSection)
  const hasHighlights = !!props.offerDetails.highlights
  const isFlashHotelOffer = props.offerDetails.type === OFFER_TYPE_HOTEL
  const shouldTriggerRemoveHighlightsExperiment = isFlashHotelOffer && hasHighlights
  const [navBarRef, navBarInView] = useInView({ triggerOnce: true, skip: !shouldTriggerRemoveHighlightsExperiment })
  const removeHighlightsVariationEnabled = getOptimizelyEnabledDecision((removeHighlightsVariation ?? null) as string | null)
  const shouldHideHighlights = isFlashHotelOffer && hasHighlights && removeHighlightsVariationEnabled

  useEffect(() => {
    if (navBarInView && shouldTriggerRemoveHighlightsExperiment) {
      triggerRemoveHighlightsAnalytics()
    }
  }, [navBarInView, shouldTriggerRemoveHighlightsExperiment, triggerRemoveHighlightsAnalytics])

  const contentFields = getContentFields(props, isNewReviewsPanelEnabled, shouldHideHighlights)
  const defaultOpenField = contentFields.find(field => field.open)

  const [openFields, setOpenFields] = useState<Record<string, boolean>>(defaultOpenField ? { [defaultOpenField.id]: true } : {})
  const [headerSelected, setHeaderSelected] = useState<string | undefined>(defaultOpenField ? defaultOpenField?.id : undefined)
  const onGroupInView = useCallback((id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
      if (id === 'customer-reviews') {
        triggerReviewsExperiment()
      }
    }
  }, [triggerReviewsExperiment])

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToId(id, -71)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    if (READ_MORE_TRACKING_MAP[id]) {
      const isOpening = !openFields[id]
      const subject = isOpening ? READ_MORE_TRACKING_MAP[id] : `${READ_MORE_TRACKING_MAP[id]}_close`
      trackClientEvent({
        subject,
        action: 'click',
        category: 'offer page',
        type: 'interaction',
      })
    }
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  const showModal = useContext(ModalContext)

  const onModalOpenToggle = useCallback(() => {
    showModal(<Modal
      title="About this supplier"
    >
      <TraderInformation traderInformation={props.traderInformation!} />
    </Modal>)
  }, [showModal, props.traderInformation])

  return (
    <>
      <div ref={navBarRef}>
        <NavigationBar
          contentFields={contentFields}
          onClick={onHeaderClick}
          selectedHeader={headerSelected}
        />
      </div>
      <LayoutContainer variant={isNewReviewsPanelEnabled ? 'large' : 'medium'}>
        <Sheet padding="20 0" tabletPadding="56 0">
          <VerticalSpacer gap={20}>
            {contentFields.map((contentField, index) => <React.Fragment key={contentField.id}>
              {index !== 0 && <Divider kind="secondary" direction="horizontal" />}
              <NavigableOfferContentGroup
                heading={contentField.label}
                id={contentField.id}
                onInViewChange={onGroupInView}
                initialHeight={contentField.initialHeight ?? 96}
                minimumContentHeight={contentField.minimumContentHeight ?? 0}
                open={!!openFields[contentField.id]}
                onToggle={onReadMoreToggle}
                collapsable={contentField.collapsable}
              >
                <BodyText variant="medium">
                  {contentField.contentComponent}
                </BodyText>
              </NavigableOfferContentGroup>
            </React.Fragment>)}
            {props.traderInformation && (
              <div id="trader-information">
                <TextLink size="medium" onClick={onModalOpenToggle}>
                  About this supplier
                </TextLink>
              </div>
            )}
          </VerticalSpacer>
        </Sheet>
      </LayoutContainer>
    </>
  )
}

export default NavigableOfferContent
